const FIRST_TOUCH_KEY = 'eien_first_touch';
const LAST_TOUCH_KEY = 'eien_last_touch';
const FIRST_TOUCH_TTL_MS = 90 * 24 * 60 * 60 * 1000;

const ATTRIBUTION_KEYS = [
  'source',
  'medium',
  'campaign',
  'content',
  'term',
  'landing_page',
  'referrer',
  'gclid',
  'fbclid',
];

const UTM_TO_ATTRIBUTION = {
  utm_source: 'source',
  utm_medium: 'medium',
  utm_campaign: 'campaign',
  utm_content: 'content',
  utm_term: 'term',
};

const FALLBACK_ATTRIBUTION = {
  source: 'direct',
  medium: 'none',
  campaign: 'none',
  content: 'none',
  term: 'none',
  landing_page: '/',
  referrer: 'none',
  gclid: 'none',
  fbclid: 'none',
};

const hasWindow = () => typeof window !== 'undefined';

const safeGet = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    // Storage can be unavailable in private browsing or restrictive contexts.
  }
};

const parseStored = (storage, key) => {
  const value = safeGet(storage, key);
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const sanitize = (value, fallback = 'none') => {
  if (value == null || value === '') return fallback;
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_./:-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 100) || fallback;
};

const normalizeReferrer = (referrer) => {
  if (!referrer) return 'none';

  try {
    const ref = new URL(referrer);
    if (hasWindow() && ref.hostname === window.location.hostname) return 'internal';
    return sanitize(ref.hostname.replace(/^www\./, ''));
  } catch {
    return 'none';
  }
};

const getLandingPage = () => {
  if (!hasWindow()) return '/';
  return window.location.pathname || '/';
};

const buildCurrentTouch = () => {
  if (!hasWindow()) return FALLBACK_ATTRIBUTION;

  const params = new URLSearchParams(window.location.search);
  const touch = {
    ...FALLBACK_ATTRIBUTION,
    landing_page: getLandingPage(),
    referrer: normalizeReferrer(document.referrer),
  };

  Object.entries(UTM_TO_ATTRIBUTION).forEach(([utmKey, attributionKey]) => {
    touch[attributionKey] = sanitize(params.get(utmKey), FALLBACK_ATTRIBUTION[attributionKey]);
  });

  touch.gclid = sanitize(params.get('gclid'), FALLBACK_ATTRIBUTION.gclid);
  touch.fbclid = sanitize(params.get('fbclid'), FALLBACK_ATTRIBUTION.fbclid);

  if (touch.gclid !== 'none' && touch.source === 'direct') {
    touch.source = 'google';
    touch.medium = 'cpc';
  }

  const hasCampaignSignal =
    touch.source !== 'direct' ||
    touch.medium !== 'none' ||
    touch.campaign !== 'none' ||
    touch.gclid !== 'none' ||
    touch.fbclid !== 'none';

  if (!hasCampaignSignal && touch.referrer !== 'none' && touch.referrer !== 'internal') {
    touch.source = touch.referrer;
    touch.medium = 'referral';
  }

  return touch;
};

const withMeta = (touch) => ({
  value: ATTRIBUTION_KEYS.reduce((acc, key) => {
    acc[key] = sanitize(touch[key], FALLBACK_ATTRIBUTION[key] || 'none');
    return acc;
  }, {}),
  created_at: Date.now(),
});

export const initAttribution = () => {
  if (!hasWindow()) return;

  const currentTouch = withMeta(buildCurrentTouch());
  const existingLastTouch = parseStored(window.sessionStorage, LAST_TOUCH_KEY);
  const incomingHasSignal =
    currentTouch.value.source !== 'direct' ||
    currentTouch.value.medium !== 'none' ||
    currentTouch.value.campaign !== 'none' ||
    currentTouch.value.gclid !== 'none' ||
    currentTouch.value.fbclid !== 'none' ||
    (currentTouch.value.referrer !== 'none' && currentTouch.value.referrer !== 'internal');

  if (!existingLastTouch || incomingHasSignal) {
    safeSet(window.sessionStorage, LAST_TOUCH_KEY, JSON.stringify(currentTouch));
  }

  const existingFirstTouch = parseStored(window.localStorage, FIRST_TOUCH_KEY);
  const isExpired =
    !existingFirstTouch?.created_at ||
    Date.now() - existingFirstTouch.created_at > FIRST_TOUCH_TTL_MS;

  if (!existingFirstTouch || isExpired) {
    safeSet(window.localStorage, FIRST_TOUCH_KEY, JSON.stringify(currentTouch));
  }
};

export const getAttribution = () => {
  if (!hasWindow()) return { ...FALLBACK_ATTRIBUTION };

  const lastTouch = parseStored(window.sessionStorage, LAST_TOUCH_KEY);
  const firstTouch = parseStored(window.localStorage, FIRST_TOUCH_KEY);
  const fallback = withMeta(buildCurrentTouch());
  const selected = lastTouch?.value || firstTouch?.value || fallback.value;

  return ATTRIBUTION_KEYS.reduce((acc, key) => {
    acc[key] = sanitize(selected[key], FALLBACK_ATTRIBUTION[key] || 'none');
    return acc;
  }, {});
};

export const getFirstTouchAttribution = () => {
  if (!hasWindow()) return { ...FALLBACK_ATTRIBUTION };
  const firstTouch = parseStored(window.localStorage, FIRST_TOUCH_KEY);
  return firstTouch?.value || getAttribution();
};

export const getAttributionRef = (ctaId) => {
  const { source, medium, campaign } = getAttribution();
  return `${source}/${medium}/${campaign}/${sanitize(ctaId, 'unknown')}`;
};

export const getAttributionEmailLine = () => {
  const { source, medium, campaign, landing_page: landingPage } = getAttribution();
  return `Origen: ${source} / ${medium} / ${campaign} / ${landingPage}`;
};

export const getCalendlyUrl = (baseUrl, ctaId) => {
  const url = new URL(baseUrl);
  const attribution = getAttribution();

  url.searchParams.set('utm_source', attribution.source);
  url.searchParams.set('utm_medium', attribution.medium);
  url.searchParams.set('utm_campaign', attribution.campaign);
  url.searchParams.set('utm_content', sanitize(ctaId, 'unknown'));
  if (attribution.term !== 'none') url.searchParams.set('utm_term', attribution.term);
  if (attribution.gclid !== 'none') url.searchParams.set('gclid', attribution.gclid);
  if (attribution.fbclid !== 'none') url.searchParams.set('fbclid', attribution.fbclid);

  return url.toString();
};

export const appendWhatsAppRef = (message, ctaId) => {
  const ref = getAttributionRef(ctaId);
  return `${message}\n\n[ref: ${ref}]`;
};

export const buildWhatsAppUrl = ({ number, message, ctaId }) => {
  const url = new URL(`https://wa.me/${number}`);
  url.searchParams.set('text', appendWhatsAppRef(message, ctaId));
  return url.toString();
};

export const buildMailtoUrl = ({ email, subject, ctaId }) => {
  const ref = getAttributionRef(ctaId);
  const url = new URL(`mailto:${email}`);
  url.searchParams.set('subject', `${subject} [ref: ${ref}]`);
  return url.toString();
};
